/*
 * td-market-guard.js — market variant guard (last line of defence).
 *
 * Two flavours of Coffee 2.0 exist twice: the original variant and an "-NP" twin with different
 * ingredients and a different declaration, which may only be sold in the countries configured for
 * that twin. Every picker in the theme already resolves this server-side (td-flavour-resolve), but a
 * variant id can still reach the cart through paths Liquid never sees:
 *
 *   - hardcoded ids on the custom-coded advertorial pages
 *   - ?variant=<id> deep links from feeds, ads and saved links
 *   - app widgets that build their own add-to-cart request (UpCart)
 *   - carts assembled days ago, before a fix shipped
 *   - any page someone writes next month
 *
 * This guard sits in front of every add-to-cart the BROWSER makes and swaps a variant id for its
 * own twin when the current country calls for it. It is deliberately narrow:
 *
 *   - it only ever maps an id that appears in the table emitted by snippets/td-market-guard.liquid
 *   - it never blocks a request, never changes quantity, price, or any property except _FlavorIds
 *   - the cart sweep removes a line only when it holds a variant not sold in this country at all
 *     (td-market-guard-blocked) and the map has no other version to swap it for
 *   - every failure path falls through to the original request untouched
 *
 * What it cannot cover: work done server-side after checkout (Simple Bundles expanding a parent
 * variant into components) and Shopify's own checkout UI extensions. Those need the bundle mapping
 * and a cart/checkout validation function respectively.
 *
 * Debugging: window.__tdMarketGuard.log lists every rewrite made on this page.
 */
(function () {
  'use strict';

  if (window.__tdMarketGuard) return;

  var CONFIG = { country: '', map: {} };
  try {
    var el = document.getElementById('td-market-guard-data');
    if (el && el.textContent) {
      var parsed = JSON.parse(el.textContent);
      if (parsed && typeof parsed === 'object') {
        CONFIG.country = String(parsed.country || '');
        CONFIG.map = parsed.map && typeof parsed.map === 'object' ? parsed.map : {};
        // Dead-id repairs that apply in every country; a market swap wins.
        if (parsed.heal && typeof parsed.heal === 'object') {
          CONFIG.map = Object.assign({}, parsed.heal, CONFIG.map);
        }
      }
    }
  } catch (e) { /* keep the empty config: the guard becomes a no-op */ }

  // Variants not sold in this country (snippets/td-market-guard.liquid). A cart line holding one,
  // directly or in _FlavorIds, is swapped when MAP has another version and removed when it has not.
  var BLOCKED = {};
  try {
    var blockedEl = document.getElementById('td-market-guard-blocked');
    var blockedList = blockedEl && blockedEl.textContent ? JSON.parse(blockedEl.textContent) : [];
    if (Array.isArray(blockedList)) blockedList.forEach(function (id) { BLOCKED[String(id)] = true; });
  } catch (e) { /* no list: nothing is removed */ }

  var MAP = CONFIG.map;
  var ACTIVE = Object.keys(MAP).length > 0 || Object.keys(BLOCKED).length > 0;
  var GID_PREFIX = 'gid://shopify/ProductVariant/';
  var LOG = [];

  function note(what, from, to) {
    try {
      LOG.push({ what: what, from: String(from), to: String(to), at: new Date().toISOString() });
      if (window.console && console.info) console.info('[td-market-guard] ' + what + ': ' + from + ' -> ' + to);
    } catch (e) {}
  }

  /* ---------- id mapping ---------- */

  function mapId(value) {
    if (value === null || value === undefined) return value;
    var key = String(value).trim();
    if (!key) return value;
    var hit = MAP[key];
    return hit ? hit : value;
  }

  // "gid://shopify/ProductVariant/123,gid://shopify/ProductVariant/456" and bare id lists.
  function mapIdList(value) {
    if (typeof value !== 'string' || !value) return value;
    var parts = value.split(',');
    var changed = false;
    var out = parts.map(function (part) {
      var trimmed = part.trim();
      if (!trimmed) return part;
      if (trimmed.indexOf(GID_PREFIX) === 0) {
        var raw = trimmed.slice(GID_PREFIX.length);
        var mapped = mapId(raw);
        if (String(mapped) !== String(raw)) { changed = true; return GID_PREFIX + mapped; }
        return part;
      }
      if (/^\d+$/.test(trimmed)) {
        var m2 = mapId(trimmed);
        if (String(m2) !== trimmed) { changed = true; return String(m2); }
      }
      return part;
    });
    return changed ? out.join(',') : value;
  }

  function isFlavorIdsKey(key) {
    return typeof key === 'string' && key.indexOf('_FlavorIds') !== -1;
  }

  function isIdKey(key) {
    return key === 'id' || key === 'variant_id' || key === 'quantity_id';
  }

  /* ---------- payload rewriters ---------- */

  function rewriteObject(obj) {
    var changed = false;
    if (!obj || typeof obj !== 'object') return false;

    if (Array.isArray(obj)) {
      for (var i = 0; i < obj.length; i++) if (rewriteObject(obj[i])) changed = true;
      return changed;
    }

    Object.keys(obj).forEach(function (key) {
      var value = obj[key];
      if (isIdKey(key) && (typeof value === 'string' || typeof value === 'number')) {
        var mapped = mapId(value);
        if (String(mapped) !== String(value)) {
          obj[key] = typeof value === 'number' ? Number(mapped) : String(mapped);
          note('json ' + key, value, mapped);
          changed = true;
        }
        return;
      }
      if (isFlavorIdsKey(key) && typeof value === 'string') {
        var list = mapIdList(value);
        if (list !== value) { obj[key] = list; note('json ' + key, value, list); changed = true; }
        return;
      }
      if (value && typeof value === 'object') {
        if (rewriteObject(value)) changed = true;
      }
    });
    return changed;
  }

  function rewriteJsonString(text) {
    var data;
    try { data = JSON.parse(text); } catch (e) { return null; }
    if (!rewriteObject(data)) return null;
    try { return JSON.stringify(data); } catch (e) { return null; }
  }

  // Rebuilt in the original order: a reordered body is harder to debug and some readers care.
  function rewriteSearchParams(params) {
    var changed = false;
    var entries = [];
    params.forEach(function (value, key) {
      var isId = isIdKey(key) || /\[(id|variant_id)\]$/.test(key);
      var isFlv = isFlavorIdsKey(key);
      var next = value;
      if (isFlv) next = mapIdList(value);
      else if (isId) next = String(mapId(value));
      if (next !== value) { note('form ' + key, value, next); changed = true; }
      entries.push([key, next]);
    });
    if (!changed) return false;
    entries.forEach(function (pair, index) {
      if (index === 0) params.set(pair[0], pair[1]); else params.append(pair[0], pair[1]);
    });
    // set() on the first key kept its position but left the old duplicates behind it; rebuild cleanly.
    var keys = [];
    params.forEach(function (_v, k) { if (keys.indexOf(k) === -1) keys.push(k); });
    keys.forEach(function (k) { params.delete(k); });
    entries.forEach(function (pair) { params.append(pair[0], pair[1]); });
    return true;
  }

  function rewriteUrlEncoded(text) {
    var params;
    try { params = new URLSearchParams(text); } catch (e) { return null; }
    if (!rewriteSearchParams(params)) return null;
    return params.toString();
  }

  function rewriteFormData(fd) {
    var changed = false;
    var entries = [];
    try { fd.forEach(function (value, key) { entries.push([key, value]); }); } catch (e) { return false; }
    entries.forEach(function (pair) {
      var key = pair[0], value = pair[1];
      if (typeof value !== 'string') return;
      var isId = isIdKey(key) || /\[(id|variant_id)\]$/.test(key);
      var isFlv = isFlavorIdsKey(key);
      if (!isId && !isFlv) return;
      var mapped = isFlv ? mapIdList(value) : String(mapId(value));
      if (mapped !== value) { fd.set(key, mapped); note('formdata ' + key, value, mapped); changed = true; }
    });
    return changed;
  }

  // Returns the replacement body, or null when nothing changed / the type is not handled.
  function rewriteBody(body, contentType) {
    try {
      if (body === null || body === undefined) return null;
      if (typeof body === 'string') {
        var looksJson = (contentType && contentType.indexOf('json') !== -1) || /^\s*[\{\[]/.test(body);
        return looksJson ? rewriteJsonString(body) : rewriteUrlEncoded(body);
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return rewriteFormData(body) ? body : null;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return rewriteSearchParams(body) ? body : null;
      }
      if (typeof body === 'object' && !(typeof Blob !== 'undefined' && body instanceof Blob)) {
        return rewriteObject(body) ? body : null;
      }
    } catch (e) { /* fall through untouched */ }
    return null;
  }

  /* ---------- request matching ---------- */

  // Shopify's own "Buy it now" and dynamic checkout buttons do not touch /cart/add.js at all:
  // they build the cart through the Storefront API (cartCreate / cartLinesAdd) and go straight to
  // checkout, so no page load and no cart sweep ever happens. EasyGift's BuyNowInterceptor rides on
  // the same call. Rewriting the merchandise gid in that request body is the only place to catch it.
  function isStorefrontApiUrl(url) {
    try {
      if (!url) return false;
      var path = String(url).split('?')[0].split('#')[0];
      return /\/api\/[^/]*\/?graphql\.json$/.test(path);
    } catch (e) { return false; }
  }

  // Raw-string rewrite: only ids present in the map are touched, so any query shape is safe.
  function rewriteGidsInText(text) {
    if (typeof text !== 'string' || text.indexOf('ProductVariant') === -1) return null;
    var changed = false;
    var out = text.replace(/(gid:\/\/shopify\/ProductVariant\/|gid%3A%2F%2Fshopify%2FProductVariant%2F)(\d+)/g,
      function (whole, prefix, id) {
        var mapped = MAP[id];
        if (!mapped) return whole;
        changed = true;
        note('storefront api', id, mapped);
        return prefix + mapped;
      });
    return changed ? out : null;
  }

  /* /cart/update.js is not only for quantities: `{"updates":{"<variantId>":1}}` ADDS that variant to
   * the cart. Verified against the live cart. Any app using that path bypassed every hook we had, so
   * it is now rewritten too: the mapped id gets the quantity and the original id is explicitly set to
   * 0, because update.js quantities are absolute — without that the old line would simply stay.
   * Line-key forms ("123:abc") are left alone; only bare numeric ids that are in the map are touched.
   */
  function isCartUpdateUrl(url) {
    try {
      if (!url) return false;
      var path = String(url).split('?')[0].split('#')[0];
      return /\/cart\/update(\.js)?\/?$/.test(path);
    } catch (e) { return false; }
  }

  function rewriteUpdates(updates) {
    if (!updates || typeof updates !== 'object' || Array.isArray(updates)) return false;
    var changed = false;
    Object.keys(updates).forEach(function (key) {
      if (!/^\d+$/.test(key)) return;
      var mapped = MAP[key];
      if (!mapped) return;
      var qty = updates[key];
      updates[mapped] = (typeof updates[mapped] === 'number' ? updates[mapped] : 0) + (typeof qty === 'number' ? qty : Number(qty) || 0);
      updates[key] = 0;
      note('cart update', key, mapped);
      changed = true;
    });
    return changed;
  }

  function rewriteUpdateBody(body, contentType) {
    try {
      if (typeof body === 'string') {
        var looksJson = (contentType && contentType.indexOf('json') !== -1) || /^\s*\{/.test(body);
        if (looksJson) {
          var data;
          try { data = JSON.parse(body); } catch (e) { return null; }
          if (!data || !rewriteUpdates(data.updates)) return null;
          return JSON.stringify(data);
        }
        var params;
        try { params = new URLSearchParams(body); } catch (e) { return null; }
        var pairs = [], changed = false;
        params.forEach(function (value, key) {
          var m = key.match(/^updates\[(\d+)\]$/);
          if (m && MAP[m[1]]) {
            pairs.push(['updates[' + MAP[m[1]] + ']', value]);
            pairs.push(['updates[' + m[1] + ']', '0']);
            note('cart update', m[1], MAP[m[1]]);
            changed = true;
          } else pairs.push([key, value]);
        });
        if (!changed) return null;
        var out = new URLSearchParams();
        pairs.forEach(function (kv) { out.append(kv[0], kv[1]); });
        return out.toString();
      }
      if (typeof body === 'object' && body && !(typeof Blob !== 'undefined' && body instanceof Blob) &&
          !(typeof FormData !== 'undefined' && body instanceof FormData)) {
        return rewriteUpdates(body.updates) ? body : null;
      }
    } catch (e) {}
    return null;
  }

  function isCartAddUrl(url) {
    try {
      if (!url) return false;
      var path = String(url).split('?')[0].split('#')[0];
      return /\/cart\/add(\.js)?\/?$/.test(path);
    } catch (e) { return false; }
  }

  /* ---------- patches ---------- */

  var NATIVE_FETCH = (typeof window.fetch === 'function') ? window.fetch.bind(window) : null;

  function patchFetch() {
    if (typeof window.fetch !== 'function') return;
    var original = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url);
        if (isStorefrontApiUrl(url)) {
          if (init && typeof init.body === 'string') {
            var gnext = rewriteGidsInText(init.body);
            if (gnext !== null) init = Object.assign({}, init, { body: gnext });
          } else if (typeof Request !== 'undefined' && input instanceof Request) {
            var greq = input;
            return greq.clone().text().then(function (text) {
              var rep = rewriteGidsInText(text);
              if (rep === null) return original.call(window, greq, init);
              return original.call(window, new Request(greq, { body: rep }), init);
            }).catch(function () { return original.call(window, input, init); });
          }
        }
        if (isCartUpdateUrl(url) && init && init.body !== undefined) {
          var uct = '';
          try {
            if (init.headers && typeof init.headers.get === 'function') uct = init.headers.get('Content-Type') || '';
          } catch (e) {}
          var unext = rewriteUpdateBody(init.body, uct);
          if (unext !== null) init = Object.assign({}, init, { body: unext });
        }
        if (isCartAddUrl(url)) {
          recordAdd('fetch');
          if (init && init.body !== undefined) {
            var ct = '';
            try {
              if (init.headers) {
                if (typeof init.headers.get === 'function') ct = init.headers.get('Content-Type') || '';
                else if (typeof init.headers === 'object') {
                  Object.keys(init.headers).forEach(function (k) {
                    if (k.toLowerCase() === 'content-type') ct = String(init.headers[k]);
                  });
                }
              }
            } catch (e) {}
            var next = rewriteBody(init.body, ct);
            if (next !== null) init = Object.assign({}, init, { body: next });
          } else if (typeof Request !== 'undefined' && input instanceof Request) {
            // Body already consumed into the Request object: read, rewrite, rebuild.
            var req = input;
            return req.clone().text().then(function (text) {
              var replaced = rewriteBody(text, req.headers && req.headers.get('Content-Type'));
              if (replaced === null) return original.call(window, req, init);
              return original.call(window, new Request(req, { body: replaced }), init);
            }).catch(function () { return original.call(window, input, init); });
          }
        }
      } catch (e) { /* never break an add */ }
      var out = original.call(window, input, init);
      try {
        if (isCartAddUrl(typeof input === 'string' ? input : (input && input.url))) {
          // Bundle apps append their component lines just after this call returns.
          out.then(function () { setTimeout(sweepCart, 900); }, function () {});
        }
      } catch (e) {}
      return out;
    };
  }

  function patchXhr() {
    if (typeof XMLHttpRequest === 'undefined') return;
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__tdGuardCartAdd = isCartAddUrl(url);
        this.__tdGuardSfApi = isStorefrontApiUrl(url);
        this.__tdGuardCartUpdate = isCartUpdateUrl(url);
      } catch (e) { this.__tdGuardCartAdd = false; this.__tdGuardSfApi = false; }
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this.__tdGuardCartUpdate && body !== undefined && body !== null) {
          var unext = rewriteUpdateBody(body, this.__tdGuardContentType || '');
          if (unext !== null) return send.call(this, unext);
        }
        if (this.__tdGuardSfApi && typeof body === 'string') {
          var gnext = rewriteGidsInText(body);
          if (gnext !== null) return send.call(this, gnext);
        }
        if (this.__tdGuardCartAdd && body !== undefined && body !== null) {
          recordAdd('xhr');
          var next = rewriteBody(body, this.__tdGuardContentType || '');
          if (next !== null) return send.call(this, next);
        }
      } catch (e) {}
      return send.apply(this, arguments);
    };
    var setHeader = XMLHttpRequest.prototype.setRequestHeader;
    XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
      try { if (String(name).toLowerCase() === 'content-type') this.__tdGuardContentType = String(value); } catch (e) {}
      return setHeader.apply(this, arguments);
    };
  }

  // Native (non-JS) form posts, and forms whose fields are read by other scripts.
  function rewriteFormFields(form) {
    var changed = false;
    try {
      var fields = form.querySelectorAll('input[name="id"], input[name="variant_id"], input[name*="_FlavorIds"], select[name="id"]');
      Array.prototype.forEach.call(fields, function (field) {
        var value = field.value;
        if (typeof value !== 'string' || !value) return;
        var mapped = isFlavorIdsKey(field.name) ? mapIdList(value) : String(mapId(value));
        if (mapped !== value) { field.value = mapped; note('form field ' + field.name, value, mapped); changed = true; }
      });
    } catch (e) {}
    return changed;
  }

  function isCartAddForm(form) {
    try {
      var action = form.getAttribute('action') || '';
      if (isCartAddUrl(action)) return true;
      // Dawn's product form posts to /cart/add through its own JS but still carries name="id".
      return !!form.querySelector('input[name="id"]') && /cart/.test(action);
    } catch (e) { return false; }
  }

  function patchForms() {
    try {
      document.addEventListener('submit', function (event) {
        var form = event.target;
        if (form && form.tagName === 'FORM' && isCartAddForm(form)) { recordAdd('form'); rewriteFormFields(form); }
      }, true);
    } catch (e) {}

    try {
      var nativeSubmit = HTMLFormElement.prototype.submit;
      HTMLFormElement.prototype.submit = function () {
        try { if (isCartAddForm(this)) { recordAdd('form'); rewriteFormFields(this); } } catch (e) {}
        return nativeSubmit.apply(this, arguments);
      };
    } catch (e) {}
  }

  // ?variant=<old id> deep links: Shopify preselects the variant server-side, so the form arrives
  // carrying an id this market must not buy. Fix the fields as soon as they exist.
  function sweepForms() {
    try {
      var forms = document.querySelectorAll('form');
      Array.prototype.forEach.call(forms, function (form) {
        if (isCartAddForm(form)) rewriteFormFields(form);
      });
    } catch (e) {}
  }

  /* ---------- cart sweep ----------
   * A cart built before a fix shipped still holds the wrong variant, and nothing re-adds it, so no
   * add-to-cart hook can ever see it. Once per page load we read the cart and, for any line whose
   * variant is mapped, add the twin with the same quantity, properties and selling plan, then drop
   * the old line. Add first, remove second: if the add fails we leave the cart exactly as it was.
   */

  var SWEEP_MAX = 10;

  function shopRoot() {
    try {
      var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      if (typeof r === 'string' && r) return r.charAt(r.length - 1) === '/' ? r : r + '/';
    } catch (e) {}
    return '/';
  }

  function mapProperties(props) {
    var out = null;
    if (!props || typeof props !== 'object') return props || undefined;
    Object.keys(props).forEach(function (key) {
      var value = props[key];
      if (isFlavorIdsKey(key) && typeof value === 'string') {
        var next = mapIdList(value);
        if (next !== value) { out = out || Object.assign({}, props); out[key] = next; }
      }
    });
    return out || props;
  }

  function sellingPlanOf(line) {
    try {
      if (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan)
        return line.selling_plan_allocation.selling_plan.id;
    } catch (e) {}
    return null;
  }

  function swapLine(root, line, targetId) {
    var payload = { id: Number(targetId), quantity: line.quantity };
    var props = mapProperties(line.properties);
    if (props && Object.keys(props).length) payload.properties = props;
    var plan = sellingPlanOf(line);
    if (plan) payload.selling_plan = plan;

    return NATIVE_FETCH(root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [payload] })
    }).then(function (res) {
      if (!res.ok) throw new Error('add failed ' + res.status);
      // Only now is it safe to drop the old line.
      return NATIVE_FETCH(root + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key || line.id, quantity: 0 })
      });
    }).then(function (res) {
      if (res && res.ok) { note('cart sweep', line.variant_id, targetId); return true; }
      // The twin is in the cart but the original would not go away: leaving both means the customer
      // pays twice. Roll the addition back so the cart is exactly as we found it.
      return NATIVE_FETCH(root + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: String(targetId), quantity: 0 })
      }).then(function () { throw new Error('remove failed, addition rolled back'); },
              function () { throw new Error('remove failed, rollback failed'); });
    });
  }

  var sweepInFlight = null;
  var lastSweepAt = 0;

  function sweepCart() {
    if (!ACTIVE || !NATIVE_FETCH) return Promise.resolve(0);
    if (sweepInFlight) return sweepInFlight;          // never run two at once
    sweepInFlight = runSweep().then(function (n) {
      sweepInFlight = null; lastSweepAt = Date.now(); return n;
    }, function () { sweepInFlight = null; lastSweepAt = Date.now(); return 0; });
    return sweepInFlight;
  }

  /* A one-line receipt written into the cart, and from there into the order:
   *   _mkt_guard = "v5|HR|swept:2"
   * Without it, an order that still holds a wrong SKU leaves three explanations open — the guard
   * never ran, it ran and found nothing, or it swapped and something put the line back. The receipt
   * tells them apart. Written at most once per cart (rewritten only when a later sweep actually
   * swaps something). /cart/update.js MERGES attributes, so PickupPointId, addressly_* and every
   * other app's attribute survive untouched — verified against the live cart before shipping this.
   */
  /* ---------- add-path trail ----------
   * The receipt used to say only which country the guard saw. When a wrong SKU still lands, that
   * leaves the surface unknown: product page, advertorial, app widget. Every intercepted add now
   * records "<how>@<path>" in sessionStorage, and the receipt carries the last three. Nothing is
   * rewritten differently because of this; it only makes the path visible in the order.
   */
  var RECEIPT_WRITES = 0;

  function trail() {
    try { return sessionStorage.getItem('_tdmg_trail') || ''; } catch (e) { return ''; }
  }

  function recordAdd(how) {
    try {
      var path = String(location.pathname || '').slice(0, 60);
      var tag = how + '@' + path;
      var parts = trail() ? trail().split('~') : [];
      if (parts.indexOf(tag) !== -1) return;
      parts.push(tag);
      while (parts.length > 3) parts.shift();
      sessionStorage.setItem('_tdmg_trail', parts.join('~'));
    } catch (e) {}
  }

  function writeReceipt(root, cart, swaps) {
    try {
      var attrs = (cart && cart.attributes) || {};
      var existing = attrs._mkt_guard;
      var t = trail();
      var value = 'v6|' + (CONFIG.country || '?') + '|swept:' + swaps + (t ? '|src=' + t : '');
      if (value.length > 240) value = value.slice(0, 240);
      // Unchanged value is not worth a request. Otherwise: always write the first one, and allow a
      // few updates per page load so a later add still shows up — capped, so this can never turn
      // into a write loop.
      if (existing === value) return;
      if (existing && RECEIPT_WRITES >= 3) return;
      RECEIPT_WRITES++;
      NATIVE_FETCH(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _mkt_guard: value } })
      }).catch(function () {});
    } catch (e) {}
  }

  /* A bundle arrives as ONE cart line whose merchandise is the pack parent — an id the pair table
   * does not carry — while the flavour it will expand into rides in properties._FlavorIds. Swapping
   * the line is therefore impossible and also wrong; the flavour list is what has to move. Carts
   * outlive a market flip (a CZ cart from before 2026-09-16 was still ordering -NP six days later),
   * and the add-time hooks only ever see the add, so the sweep has to fix what is already sitting
   * there. Properties are replaced in place: nothing is added, so a failure costs nothing and needs
   * no rollback.
   */
  function fixLineProperties(root, line, nextProps) {
    var body = { id: line.key || line.id, quantity: line.quantity, properties: nextProps };
    var plan = sellingPlanOf(line);
    if (plan) body.selling_plan = plan;
    return NATIVE_FETCH(root + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res || !res.ok) throw new Error('properties change failed');
      note('cart sweep props', (line.properties || {})._FlavorIds, nextProps._FlavorIds);
      return true;
    });
  }

  // Blocked here with no other version to swap to, checked after the swaps a line would get.
  function unsellable(id) {
    var key = String(id || '').trim().replace(/^gid:.*\//, '');
    return !!key && !!BLOCKED[key] && !MAP[key];
  }

  function holdsUnsellable(line) {
    if (unsellable(line.variant_id)) return true;
    var props = mapProperties(line.properties) || {};
    return Object.keys(props).some(function (key) {
      return isFlavorIdsKey(key) && typeof props[key] === 'string' &&
        props[key].split(',').some(unsellable);
    });
  }

  function removeLine(root, line) {
    return NATIVE_FETCH(root + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key || line.id, quantity: 0 })
    }).then(function (res) {
      if (!res || !res.ok) throw new Error('remove failed');
      note('cart sweep removed', line.variant_id, 'not sold in ' + CONFIG.country);
      return true;
    });
  }

  function runSweep() {
    var root = shopRoot();
    var cartRef = null;
    return NATIVE_FETCH(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        cartRef = cart;
        if (!cart || !cart.items || !cart.items.length) return 0;
        var todo = [];
        cart.items.forEach(function (line) {
          // Nothing of it may be sold here: no swap can fix it, so it leaves the cart.
          if (holdsUnsellable(line)) {
            if (todo.length < SWEEP_MAX) todo.push({ line: line, remove: true });
            return;
          }
          var target = MAP[String(line.variant_id)];
          if (target) {
            if (todo.length < SWEEP_MAX) todo.push({ line: line, target: target });
            return;
          }
          // Not a mapped variant, but its flavour list may still point at the wrong market's twin.
          var next = mapProperties(line.properties);
          if (next && next !== line.properties && todo.length < SWEEP_MAX) {
            todo.push({ line: line, props: next });
          }
        });
        if (!todo.length) { writeReceipt(root, cart, 0); return 0; }
        // Sequential: each swap changes the cart, so they must not race.
        var done = 0;
        return todo.reduce(function (chain, item) {
          return chain.then(function () {
            var work = item.remove
              ? removeLine(root, item.line)
              : item.props
                ? fixLineProperties(root, item.line, item.props)
                : swapLine(root, item.line, item.target);
            return work.then(function () { done++; });
          }).catch(function () { /* leave this line alone, keep going */ });
        }, Promise.resolve()).then(function () {
          if (done) {
            try {
              document.dispatchEvent(new CustomEvent('cart:updated'));
              document.dispatchEvent(new CustomEvent('tastydose:cart-updated'));
            } catch (e) {}
          }
          writeReceipt(root, cartRef, done);
          return done;
        });
      })
      .catch(function () { return 0; });
  }

  /* ---------- extra sweep triggers ----------
   * A page-load sweep alone misses the customer who leaves a tab open for hours, and any line an app
   * adds server-side moments after our add. These triggers cover both, and the checkout one is the
   * last chance before the cart becomes an order.
   */

  // Every drawer ships its own checkout button. Missing one means the pre-checkout sweep never runs
  // for the customers who use it — that is how order #372469 (UpCart drawer, IT) shipped the old SKU
  // with a clean `swept:0` receipt. Keep this list wide but anchored on real button classes, never on
  // a bare [class*="checkout"] which would match wrappers and headings too.
  var CHECKOUT_SEL = 'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], ' +
    '[data-checkout], .cart__checkout-button, #CartDrawer-Checkout, [href$="/checkout"], ' +
    '.upcart-checkout-button, [class*="upcart-checkout"], button[class*="checkout-button"], ' +
    'a[class*="checkout-button"], [id*="checkout-button"], form[action*="/checkout"] [type="submit"]';
  var checkoutArmed = true;

  function replay(el) {
    try { el.click(); }
    catch (e) {
      try { if (el.href) window.location.href = el.href; } catch (e2) {}
    }
  }

  // Runs the sweep before the customer leaves for checkout, then replays their click. Capped hard:
  // a slow or failing sweep must never keep anyone from paying.
  function onCheckoutClick(event) {
    try {
      if (!ACTIVE || !checkoutArmed) return;
      var el = event.target && event.target.closest && event.target.closest(CHECKOUT_SEL);
      if (!el) return;
      checkoutArmed = false;
      event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();

      var finished = false;
      var go = function () { if (!finished) { finished = true; replay(el); } };
      var timer = setTimeout(go, 1500);
      sweepCart().then(function () { clearTimeout(timer); go(); }, function () { clearTimeout(timer); go(); });
    } catch (e) {
      checkoutArmed = false;
    }
  }

  function armExtraTriggers() {
    try { document.addEventListener('click', onCheckoutClick, true); } catch (e) {}

    // A tab parked for a while can hold a cart built before a fix shipped.
    try {
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible' && Date.now() - lastSweepAt > 120000) sweepCart();
      });
    } catch (e) {}

    // Anything can still drop a line into the cart minutes later — an app widget, a slow bundle
    // expansion, a second tab. A light heartbeat while the tab is actually being looked at costs one
    // cart.js per minute and closes that window.
    try {
      // Tight at first — a line dropped in by an app the hooks cannot see has to be caught before the
      // customer reaches checkout, and that can be well under a minute. Then it relaxes.
      var beats = 0;
      setInterval(function () {
        if (beats >= 10) return;
        if (document.visibilityState !== 'visible') return;
        var gap = beats < 6 ? 18000 : 55000;
        if (Date.now() - lastSweepAt < gap) return;
        beats++;
        sweepCart();
      }, 20000);
    } catch (e) {}

    // Apps that expand or append lines do it right after our add; give them a moment, then re-check.
    try {
      ['cart:updated', 'tastydose:cart-updated'].forEach(function (name) {
        document.addEventListener(name, function () {
          if (Date.now() - lastSweepAt > 3000) setTimeout(sweepCart, 400);
        });
      });
    } catch (e) {}
  }

  patchFetch();
  patchXhr();
  patchForms();
  armExtraTriggers();

  function onReady() {
    sweepForms();
    // Deferred so it never competes with rendering; failures are swallowed inside sweepCart.
    try { setTimeout(sweepCart, 600); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
  document.addEventListener('shopify:section:load', sweepForms);

  window.__tdMarketGuard = {
    version: 11,
    active: ACTIVE,
    country: CONFIG.country,
    map: MAP,
    blocked: BLOCKED,
    log: LOG,
    mapId: mapId,
    mapIdList: mapIdList,
    rewriteBody: rewriteBody,
    isCartAddUrl: isCartAddUrl,
    isCartUpdateUrl: isCartUpdateUrl,
    rewriteUpdateBody: rewriteUpdateBody,
    isStorefrontApiUrl: isStorefrontApiUrl,
    rewriteGidsInText: rewriteGidsInText,
    sweepForms: sweepForms,
    checkoutSelector: CHECKOUT_SEL,
    sweepCart: sweepCart,
    writeReceipt: writeReceipt,
    fixLineProperties: fixLineProperties,
    recordAdd: recordAdd,
    trail: trail,
    onCheckoutClick: onCheckoutClick,
    mapProperties: mapProperties
  };
})();
